import React, { useState, useEffect } from 'react';
import { Tabs, Button, Dropdown, message } from 'antd';
import {
  FileTextOutlined,
  FileExcelOutlined,
  DownloadOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  AuditOutlined,
  DashboardOutlined,
  FilePdfOutlined,
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import Layout from '../../components/Layout';
import PageHeadingBanner from '../../components/shared/PageHeadingBanner';
import DashboardTab from './DashboardTab';
import ReportsTab from './ReportsTab';
import ApprovalQueueTab from './ApprovalQueueTab';
import SchedulesTab from './SchedulesTab';
import AuditTrailTab from './AuditTrailTab';
import GenerateReportModal from './GenerateReportModal';
import { getInternalReportStats, exportInternalReports } from '../../services/internalReportingService';
import './InternalReporting.css';

const InternalReporting = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [generateModalVisible, setGenerateModalVisible] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [stats, setStats] = useState({
    totalTransactions: "₦0.00",
    accountBalances: "₦0.00",
    loanPortfolio: "₦0.00",
    revenue: "₦0.00"
  });

  const fetchStats = async () => {
    try {
      const res = await getInternalReportStats();
      if (res?.status) {
        setStats(res.data);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const getExportData = async () => {
    setExporting(true);
    try {
      const res = await exportInternalReports({});
      if (!res?.status || !res.data?.length) {
        message.warning('No records to export');
        return null;
      }
      return res.data.map((report) => ({
        'Report Name': report.reportName || '',
        'Report Type': report.reportType || '',
        'Period Start': report.startDate || '',
        'Period End': report.endDate || '',
        'Status': report.status || '',
        'Generated By': report.generatedBy || '',
        'Generation Date': report.generatedAt || '',
        'Approved By': report.approvedBy || '',
        'Approval Date': report.approvedAt || '',
      }));
    } finally {
      setExporting(false);
    }
  };

  const handleExportCSV = async () => {
    const data = await getExportData();
    if (!data) return;
    const ws = XLSX.utils.json_to_sheet(data);
    const csvOutput = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob([csvOutput], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `internal_reports_${new Date().toISOString().split('T')[0]}.csv`);
    message.success('CSV exported successfully');
  };

  const handleExportExcel = async () => {
    const data = await getExportData();
    if (!data) return;
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Internal Reports');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `internal_reports_${new Date().toISOString().split('T')[0]}.xlsx`);
    message.success('Excel file exported successfully');
  };

  const handleExportPDF = async () => {
    const data = await getExportData();
    if (!data) return;
    const doc = new jsPDF();
    doc.text('Internal Reporting Summary', 14, 15);
    const tableColumn = Object.keys(data[0]);
    const tableRows = data.map(obj => Object.values(obj));
    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 20,
      theme: 'grid',
      styles: { fontSize: 8 },
    });
    doc.save(`internal_reports_${new Date().toISOString().split('T')[0]}.pdf`);
    message.success('PDF exported successfully');
  };

  const exportMenuItems = [
    { key: 'csv', label: 'Export as CSV', icon: <DownloadOutlined />, onClick: handleExportCSV },
    { key: 'excel', label: 'Export as Excel', icon: <FileExcelOutlined />, onClick: handleExportExcel },
    { key: 'pdf', label: 'Export as PDF', icon: <FilePdfOutlined />, onClick: handleExportPDF },
  ];

  const tabItems = [
    {
      key: 'dashboard',
      label: (
        <span className="reg-tab-badge">
          <DashboardOutlined className="tab-icon" />
          Dashboard
        </span>
      ),
      children: <DashboardTab stats={stats} />,
    },
    {
      key: 'reports',
      label: (
        <span className="reg-tab-badge">
          <FileTextOutlined className="tab-icon" />
          All Reports
        </span>
      ),
      children: <ReportsTab />,
    },
    {
      key: 'approval',
      label: (
        <span className="reg-tab-badge">
          <CheckCircleOutlined className="tab-icon" />
          Approval Queue
        </span>
      ),
      children: <ApprovalQueueTab />,
    },
    {
      key: 'schedules',
      label: (
        <span className="reg-tab-badge">
          <CalendarOutlined className="tab-icon" />
          Schedules
        </span>
      ),
      children: <SchedulesTab />,
    },
    {
      key: 'audit',
      label: (
        <span className="reg-tab-badge">
          <AuditOutlined className="tab-icon" />
          Audit Trail
        </span>
      ),
      children: <AuditTrailTab />,
    },
  ];

  return (
    <Layout>
      <div className="reg-wrapper" style={{ padding: '0 24px' }}>
        <PageHeadingBanner
          icon={<FileTextOutlined />}
          title="Internal Reporting"
          subtitle="Monitor institutional operational and financial performance through detailed reports and analytics"
          actions={
            <>
              <Button
                icon={<PlusOutlined />}
                className="phb-btn-solid"
                size="large"
                onClick={() => setGenerateModalVisible(true)}
              >
                Generate Report
              </Button>
              <Dropdown menu={{ items: exportMenuItems }} trigger={['click']}>
                <Button
                  icon={<DownloadOutlined />}
                  className="phb-btn-solid"
                  size="large"
                  loading={exporting}
                  style={{ background: 'rgba(255,255,255,0.15)', borderColor: 'rgba(255,255,255,0.3)' }}
                >
                  Export
                </Button>
              </Dropdown>
            </>
          }
        />

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          className="reg-tabs"
        />

        <GenerateReportModal
          visible={generateModalVisible}
          onClose={() => setGenerateModalVisible(false)}
          onSuccess={() => {
            fetchStats();
          }}
        />
      </div>
    </Layout>
  );
};

export default InternalReporting;
